// src/admin/components/products/ProductForm.tsx
import React, { useState, useEffect, useMemo } from 'react';
import {
  X,
  Upload,
  Link as LinkIcon,
  Plus,
  ExternalLink
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import { ProductSpecification } from '../../../types';
import { ProductFormData } from '../../types';
import { ProductSpecificationsModal } from '../../../components/ProductSpecificationsModal';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css'; // Import Quill's styles
import { productsApi, categoriesApi } from '../../../utils/api';

/**
 * VariationType with 'age-size' and 'all' included.
 */
type VariationType =
  | 'none'
  | 'color'
  | 'size'
  | 'age'
  | 'color-age'
  | 'color-size'
  | 'age-size'
  | 'all';

/** Convert VariationType to DB-compatible product_type. */
function mapVariationTypeToProductType(vType: VariationType): string {
  switch (vType) {
    case 'none':
      return 'simple';
    case 'color':
      return 'color';
    case 'size':
      return 'size';
    case 'age':
      return 'age';
    case 'color-age':
      return 'color-age';
    case 'color-size':
      return 'color-size';
    case 'age-size':
      return 'age-size';
    case 'all':
      return 'color-size-age';
    default:
      return 'simple';
  }
}

interface VariationStock {
  id: string;
  color?: string;
  size?: string;
  ageRange?: string;
  stock: number;
  minStock: number;
}

interface VariationImageData {
  imageType: 'upload' | 'link';
  imageUrls: string[];         // For link-based images.
  uploadedImages: File[];      // For file uploads.
  uploadPreviews: string[];    // Preview URLs for uploaded files.
  mainImageIndex: number;
}

interface ProductFormProps {
  /** If editing an existing product, pass it here. */
  product?: any;
  /**
   * onSave is called with the FormData to submit to the server,
   * plus an optional editingId if we are editing.
   */
  onSave: (data: FormData, editingId?: string | number) => void;
  onCancel: () => void;
  // New props for subcategory support
  fetchSubcategories?: (categoryId: number) => Promise<any[]>;
  subcategories?: {[categoryId: number]: any[]};
  imageOperations?: {
    getImages: (productId: number) => Promise<any[]>;
    addImages: (productId: number, formData: FormData) => Promise<any[]>;
    deleteImage: (productId: number, imageId: number) => Promise<any[]>;
    reorderImages: (productId: number, order: { imageId: number, sortOrder: number }[]) => Promise<any[]>;
    setMainImage: (productId: number, imageId: number) => Promise<any[]>;
  };
}

// We'll use the same approach as the frontend to fix partial URLs
const backendBaseURL = import.meta.env.VITE_API_BASE_URL;
const backendBaseImagesURL = import.meta.env.VITE_API_BASE_IMAGES_URL;

// Update the getFullImageUrl function
function getFullImageUrl(image: { storage_path?: string | null; url?: string } | string): string {
  // If image is a string (legacy support), handle it directly
  if (typeof image === 'string') {
    if (!image) return 'https://via.placeholder.com/150/CCCCCC/000000?text=No+Image';
    if (image.startsWith('http://') || 
        image.startsWith('https://') || 
        image.startsWith('data:')) {
      return image;
    }
    return backendBaseImagesURL + image;
  }

  // If image is an object with storage_path and url
  if (!image) return 'https://via.placeholder.com/150/CCCCCC/000000?text=No+Image';

  // Prioritize url instead of storage_path to avoid path duplication
  const path = image.url || '';
  
  if (!path) return 'https://via.placeholder.com/150/CCCCCC/000000?text=No+Image';
  
  if (path.startsWith('http://') || 
      path.startsWith('https://') || 
      path.startsWith('data:')) {
    return path;
  }

  return backendBaseImagesURL + path;
}

// Helper: Default variation image data
const getDefaultVariationImageData = (): VariationImageData => ({
  imageType: 'upload',
  imageUrls: [],
  uploadedImages: [],
  uploadPreviews: [],
  mainImageIndex: 0,
});

export const ProductForm: React.FC<ProductFormProps> = ({ 
  product, 
  onSave, 
  onCancel, 
  fetchSubcategories, 
  subcategories,
  imageOperations 
}) => {
  // ---------- Local Form State ----------
  const [formData, setFormData] = useState<ProductFormData>(() => {
    // Provide safe defaults if `product` is missing fields
    if (product) {
      return {
        name: product.name ?? '',
        sku: product.sku ?? '',
        price: product.price ?? 0,
        categoryId: product.category_id ?? 0,
        subcategoryId: product.subcategory_id ?? 0,
        description: product.description ?? '',
        // The "gallery" array is from product.gallery on GET /api/products/:id
        gallery: product.gallery ?? [],
        // Convert numeric 0/1 to booleans if needed
        featured: !!(product.featured ?? product.featured === 1),
        bestSeller: !!(product.bestSeller ?? product.best_seller === 1),
        newArrival: !!(product.newArrival ?? product.new_arrival === 1),
        mostViewed: !!(product.mostViewed ?? product.most_viewed === 1),
        stock: product.stock ?? 0,
        minStock: product.minStock ?? product.min_stock ?? 0,
        // For variations, we rely on product.variations from the backend
        variations: product.variations ?? {
          type: 'none',
          colors: [],
          sizes: [],
          ageRanges: [],
          stockByVariation: []
        },
        // Similar products array
        similarProducts: product.similarProducts ?? [],
        specifications: product.specifications || []
      };
    }
    // If no product is passed (creating new), use blank defaults
    return {
      name: '',
      sku: '',
      price: 0,
      categoryId: 0,
      subcategoryId: 0,
      description: '',
      gallery: [],
      featured: false,
      bestSeller: false,
      newArrival: false,
      mostViewed: false,
      stock: 0,
      minStock: 0,
      variations: {
        type: 'none',
        colors: [],
        sizes: [],
        ageRanges: [],
        stockByVariation: []
      },
      similarProducts: [],
      specifications: []
    };
  });

  // Categories & all products for "Similar Products"
  const [categories, setCategories] = useState<any[]>([]);
  const [allProducts, setAllProducts] = useState<any[]>([]);

  // Global image handling (for simple products)
  const [imageType, setImageType] = useState<'upload' | 'link'>('link');
  const [imageUrls, setImageUrls] = useState<string[]>((() => {
    if (product?.gallery) {
      return product.gallery.map((url: string) => {
        // Ensure each URL has the full path with prefix
        if (url.startsWith('http://') || url.startsWith('https://') || url.startsWith('data:')) {
          return url;
        }
        return backendBaseImagesURL + url;
      });
    }
    return [];
  })());
  const [uploadedImages, setUploadedImages] = useState<File[]>([]);
  const [uploadPreviews, setUploadPreviews] = useState<string[]>([]);
  const [mainImageIndex, setMainImageIndex] = useState<number>(0);

  // Variation handling
  const [variationType, setVariationType] = useState<VariationType>(() =>
    product?.variations?.type || 'none'
  );
  const [variationStocks, setVariationStocks] = useState<VariationStock[]>((() =>
    product?.variations?.stockByVariation || []
  ));
  const [newVariationValue, setNewVariationValue] = useState('');

  // New state: Variation-specific image data (for non-simple products)
  const [variationImages, setVariationImages] = useState<{ [variationId: string]: VariationImageData }>({});

  // Additional state for specifications
  const [specifications, setSpecifications] = useState<ProductSpecification[]>(product?.specifications || []);
  const [isSpecModalOpen, setIsSpecModalOpen] = useState<boolean>(false);

  // Add a new state for tracking the selected subcategory
  const [subcategoryId, setSubcategoryId] = useState<number>(
    product?.subcategory_id || 0
  );

  // ---------- Fetch categories once ----------
  useEffect(() => {
    categoriesApi.getAll()
      .then(res => setCategories(res.data))
      .catch(err => console.error('Error fetching categories:', err));
  }, []);

  // ---------- Fetch all products once for "Similar Products" ----------
  useEffect(() => {
    productsApi.getAll()
      .then(res => setAllProducts(res.data))
      .catch(err => console.error('Error fetching products:', err));
  }, []);

  // ---------- If editing, watch for product changes ----------
  useEffect(() => {
    if (product) {
      // Variation
      const existingVariationType = product.variations?.type || 'none';
      const existingStocks = product.variations?.stockByVariation || [];
      setVariationType(existingVariationType);
      setVariationStocks(existingStocks);
      setFormData(prev => ({
        ...prev,
        variations: {
          ...prev.variations,
          type: existingVariationType
        }
      }));
      
      // For a simple product, pre-fill global images
      if (existingVariationType === 'none') {
        const gallery = product.gallery || [];
        setImageUrls(gallery.map((url: string) => {
          // Ensure each URL has the full path with prefix
          if (url.startsWith('http://') || url.startsWith('https://') || url.startsWith('data:')) {
            return url;
          }
          return backendBaseImagesURL + url;
        }));
      } else {
        // For variation products, organize images by variation_id
        if (product.images && existingStocks.length > 0) {
          const variationImagesMap: { [variationId: string]: VariationImageData } = {};
          
          // Group images by variation_id using the database IDs from stockByVariation
          existingStocks.forEach((stockItem: VariationStock) => {
            // Find images that belong to this specific variation ID
            const variationImages = product.images.filter((img: any) => 
              img.variation_id !== null && String(img.variation_id) === stockItem.id
            );
            
            if (variationImages.length > 0) {
              // Convert image URLs to full paths
              const imageUrls = variationImages.map((img: any) => {
                const url = img.url;
                if (url.startsWith('http://') || url.startsWith('https://') || url.startsWith('data:')) {
                  return url;
                }
                return backendBaseImagesURL + url;
              });
              
              // Find the primary image index
              const primaryImageIndex = variationImages.findIndex((img: any) => img.is_primary);
              
              // Initialize variation image data
              variationImagesMap[stockItem.id] = {
                imageType: 'link',
                imageUrls: imageUrls,
                uploadedImages: [],
                uploadPreviews: [],
                mainImageIndex: primaryImageIndex >= 0 ? primaryImageIndex : 0
              };
            }
          });
          
          setVariationImages(variationImagesMap);
        }
      }
    }
  }, [product]);

  // ---------- Variation Handlers ----------
  const handleVariationTypeChange = (newType: VariationType) => {
    // If editing & variation not 'none', confirm reset
    if (product && variationType !== 'none') {
      const confirmed = window.confirm(
        'Changing variation type will reset all variation data. Are you sure?'
      );
      if (!confirmed) return;
    }
    setVariationType(newType);
    setVariationStocks([]);
    // Reset variation fields in formData
    setFormData(prev => ({
      ...prev,
      variations: {
        type: newType,
        colors: newType.includes('color') || newType === 'all' ? [] : [],
        sizes: newType.includes('size') || newType === 'all' ? [] : [],
        ageRanges: newType.includes('age') || newType === 'all' ? [] : [],
        stockByVariation: []
      },
      stock: 0,
      minStock: 0
    }));
    // Clear any variation-specific images
    setVariationImages({});
  };

  const handleAddVariation = (type: 'color' | 'size' | 'ageRange') => {
    if (!newVariationValue.trim()) return;
    setFormData(prev => {
      const arrayName = type === 'ageRange' ? 'ageRanges' : `${type}s`;
      return {
        ...prev,
        variations: {
          ...prev.variations,
          [arrayName]: [...prev.variations[arrayName], newVariationValue.trim()]
        }
      };
    });
    setNewVariationValue('');
  };

  const handleRemoveVariation = (type: 'color' | 'size' | 'ageRange', value: string) => {
    setFormData(prev => {
      const arrayName = type === 'ageRange' ? 'ageRanges' : `${type}s`;
      return {
        ...prev,
        variations: {
          ...prev.variations,
          [arrayName]: prev.variations[arrayName].filter((v: string) => v !== value)
        }
      };
    });
    // Also remove from variationStocks if that value is used
    setVariationStocks(prev => prev.filter(vs => vs[type] !== value));
  };

  const handleAddVariationStock = () => {
    const newStock: VariationStock = {
      id: Date.now().toString(),
      stock: 0,
      minStock: 0
    };
    // Pre-fill from first available option if exists
    if (variationType.includes('color') || variationType === 'all') {
      newStock.color = formData.variations.colors[0];
    }
    if (variationType.includes('size') || variationType === 'all') {
      newStock.size = formData.variations.sizes[0];
    }
    if (variationType.includes('age') || variationType === 'all') {
      newStock.ageRange = formData.variations.ageRanges[0];
    }
    setVariationStocks(prev => [...prev, newStock]);
    // Initialize variationImages for the new variation row
    setVariationImages(prev => ({ ...prev, [newStock.id]: getDefaultVariationImageData() }));
  };

  const handleUpdateVariationStock = (id: string, field: keyof VariationStock, value: any) => {
    setVariationStocks(prev =>
      prev.map(vs => (vs.id === id ? { ...vs, [field]: value } : vs))
    );
  };

  const calculateTotalStock = () => {
    return variationStocks.reduce((sum, item) => sum + item.stock, 0);
  };

  const calculateMinStock = () => {
    return variationStocks.reduce((sum, item) => sum + item.minStock, 0);
  };

  // ---------- Global Image Handlers (for simple products) ----------
  const combinedGlobalImages = [...uploadPreviews, ...imageUrls];

  // ---------- Variation Images Handlers ----------
  // Update the image type for a given variation
  const updateVariationImageType = (variationId: string, newType: 'upload' | 'link') => {
    setVariationImages(prev => ({
      ...prev,
      [variationId]: {
        ...(prev[variationId] || getDefaultVariationImageData()),
        imageType: newType
      }
    }));
  };

  // Update file uploads for a given variation
  const handleVariationFileChange = (variationId: string, files: FileList | null) => {
    if (!files) return;
    const fileArray = Array.from(files);
    fileArray.forEach(file => {
      const reader = new FileReader();
      reader.onloadend = () => {
        setVariationImages(prev => {
          const curr = prev[variationId] || getDefaultVariationImageData();
          return {
            ...prev,
            [variationId]: {
              ...curr,
              uploadedImages: [...curr.uploadedImages, file],
              uploadPreviews: [...curr.uploadPreviews, reader.result as string]
            }
          };
        });
      };
      reader.readAsDataURL(file);
    });
  };

  // Update URL changes for a given variation
  const handleVariationUrlChange = (variationId: string, index: number, value: string) => {
    setVariationImages(prev => {
      const curr = prev[variationId] || getDefaultVariationImageData();
      const newUrls = [...curr.imageUrls];
      newUrls[index] = value;
      return { ...prev, [variationId]: { ...curr, imageUrls: newUrls } };
    });
  };

  // Remove a URL for a given variation
  const handleRemoveVariationUrl = (variationId: string, index: number) => {
    setVariationImages(prev => {
      const curr = prev[variationId] || getDefaultVariationImageData();
      const newUrls = curr.imageUrls.filter((_, i) => i !== index);
      let newMain = curr.mainImageIndex;
      if (curr.mainImageIndex === index) newMain = 0;
      return { ...prev, [variationId]: { ...curr, imageUrls: newUrls, mainImageIndex: newMain } };
    });
  };

  // Remove an uploaded file for a given variation
  const handleRemoveVariationFile = (variationId: string, index: number) => {
    setVariationImages(prev => {
      const curr = prev[variationId] || getDefaultVariationImageData();
      const newUploaded = curr.uploadedImages.filter((_, i) => i !== index);
      const newPreviews = curr.uploadPreviews.filter((_, i) => i !== index);
      let newMain = curr.mainImageIndex;
      if (curr.mainImageIndex === index) newMain = 0;
      return { ...prev, [variationId]: { ...curr, uploadedImages: newUploaded, uploadPreviews: newPreviews, mainImageIndex: newMain } };
    });
  };

  // Upload new images for a specific variation
  const handleUploadVariationImages = async (variationId: string) => {
    if (!product?.id) {
      toast.error('Please save the product first before uploading images');
      return;
    }

    const imgData = variationImages[variationId];
    if (!imgData || (imgData.uploadedImages.length === 0 && imgData.imageUrls.length === 0)) {
      toast.error('No images to upload');
      return;
    }

    const formData = new FormData();

    // Handle uploaded files for this variation
    if (imgData.uploadedImages.length > 0) {
      imgData.uploadedImages.forEach((file) => {
        formData.append(`variation_images_${variationId}`, file);
      });
    }

    // Handle URL-based images for this variation
    if (imgData.imageUrls.length > 0) {
      const variationImageUrls: { [varId: string]: string[] } = {};
      
      // Reorder URLs so the main image is first
      const reorderedUrls = [...imgData.imageUrls];
      if (imgData.mainImageIndex > 0 && imgData.mainImageIndex < reorderedUrls.length) {
        const mainImage = reorderedUrls.splice(imgData.mainImageIndex, 1)[0];
        reorderedUrls.unshift(mainImage);
      }
      
      variationImageUrls[variationId] = reorderedUrls;
      formData.append('variation_image_urls', JSON.stringify(variationImageUrls));
    }

    try {
      // Use the imageOperations prop to add images
      await imageOperations?.addImages(product.id, formData);
      
      toast.success('Variation images uploaded successfully');
      
      // Clear the uploaded images and previews for this variation
      setVariationImages(prev => ({
        ...prev,
        [variationId]: {
          ...imgData,
          uploadedImages: [],
          uploadPreviews: []
        }
      }));
      
    } catch (error) {
      console.error('Failed to upload variation images:', error);
      toast.error('Failed to upload images');
    }
  };

  // Set main image for a specific variation in real-time
  const handleSetVariationMainImageRealTime = async (variationId: string, imageIndex: number) => {
    const imgData = variationImages[variationId];
    if (!imgData || !product?.id) return;

    // Update locally for immediate feedback
    setVariationImages(prev => ({
      ...prev,
      [variationId]: {
        ...imgData,
        mainImageIndex: imageIndex
      }
    }));

    toast.success('Main image updated for this variation');
  };

  // ---------- Handle Specifications ----------
  const handleAddSpecification = () => {
    setSpecifications(prev => [
      ...prev,
      {
        id: Date.now(), // Temporary ID for new specifications
        product_id: product?.id || 0,
        spec_key: '',
        spec_value: '',
        sort_order: prev.length,
        created_at: new Date(),
        updated_at: new Date()
      }
    ]);
  };

  const handleSpecificationChange = (id: number, field: 'spec_key' | 'spec_value', value: string) => {
    setSpecifications(prev =>
      prev.map(spec =>
        spec.id === id ? { ...spec, [field]: value } : spec
      )
    );
  };

  const handleRemoveSpecification = (id: number) => {
    setSpecifications(prev => prev.filter(spec => spec.id !== id));
  };

  // Update image handlers to use new endpoints
  const handleImageOperation = async (operation: string, ...args: any[]) => {
    try {
      if (!imageOperations || !product?.id) return;
      
      switch (operation) {
        case 'delete':
          await imageOperations.deleteImage(product.id, args[0]);
          break;
        case 'reorder':
          await imageOperations.reorderImages(product.id, args[0]);
          break;
        case 'setMain':
          await imageOperations.setMainImage(product.id, args[0]);
          break;
      }
      
      // Refresh images after operation
      const updatedImages = await imageOperations.getImages(product.id);
      setImageUrls(updatedImages.map(img => {
        // Ensure URLs have the full path
        const url = img.url || '';
        if (url.startsWith('http://') || url.startsWith('https://') || url.startsWith('data:')) {
          return url;
        }
        return backendBaseImagesURL + url;
      }));
    } catch (error) {
      console.error('Image operation failed:', error);
      toast.error('Failed to update images');
    }
  };

  // Modify existing image handlers
  const handleRemoveImage = async (imageId: number) => {
    if (!product?.id) {
      toast.error('Product ID is required');
      return;
    }

    // Show confirmation dialog
    const confirmed = window.confirm('Are you sure you want to delete this image?');
    if (!confirmed) return;

    try {
      await imageOperations?.deleteImage(product.id, imageId);
      toast.success('Image deleted successfully');
      
      // Refresh images after deletion
      const updatedImages = await imageOperations?.getImages(product.id);
      if (product.images) {
        product.images = updatedImages; // Update the product's images array
      }
    } catch (error) {
      console.error('Failed to delete image:', error);
      toast.error('Failed to delete image');
    }
  };

  const handleReorderImages = (order: { imageId: number, sortOrder: number }[]) => 
    handleImageOperation('reorder', order);

  // Add this function in the ProductForm component
  const handleSetMainImageRealTime = async (imageId: number) => {
    if (!product?.id) {
      toast.error('Product ID is required');
      return;
    }

    try {
      // Call the setMainImage endpoint directly
      const updatedImages = await imageOperations?.setMainImage(product.id, imageId);
      
      // Update the local state to reflect the change
      if (product.images) {
        product.images = updatedImages;
      }
      
      toast.success('Main image updated successfully');
    } catch (error) {
      console.error('Failed to update main image:', error);
      toast.error('Failed to update main image');
    }
  };

  // ---------- Submit Handler ----------
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Debug log to check subcategoryId
    console.log('Form data before submission:', {
      name: formData.name,
      categoryId: formData.categoryId,
      subcategoryId: formData.subcategoryId,
      variations: formData.variations?.type
    });

    const data = new FormData();

    // VariationType => product_type
    const finalProductType = mapVariationTypeToProductType(variationType);

    // Handle images according to backend expectations
    if (variationType === 'none') {
      // Handle uploaded files
      if (uploadedImages.length > 0) {
        uploadedImages.forEach(file => {
          data.append('productImages', file);
        });
      }

      // Handle image URLs
      if (imageUrls.length > 0) {
        data.append('urls', JSON.stringify(imageUrls));
      }

      // Set main image index
      data.append('mainImageIndex', String(mainImageIndex));
    } else {
      // Handle variation images for non-simple products
      console.log('🔍 Frontend: Processing variation images...');
      console.log('🔍 Frontend: variationStocks:', variationStocks);
      console.log('🔍 Frontend: variationImages:', variationImages);
      
      const variationImageUrls: { [localId: string]: string[] } = {};
      
      // Create mapping from database IDs to local IDs for backend compatibility
      const dbToLocalMapping: { [dbId: string]: string } = {};
      const localToDbMapping: { [localId: string]: string } = {};
      
      // Generate local IDs for each variation stock item
      variationStocks.forEach((stock, index) => {
        const localId = `local_${Date.now()}_${index}`;
        dbToLocalMapping[stock.id] = localId;
        localToDbMapping[localId] = stock.id;
        console.log(`🔍 Frontend: Mapping DB ID ${stock.id} -> Local ID ${localId}`);
      });
      
      console.log('🔍 Frontend: dbToLocalMapping:', dbToLocalMapping);
      console.log('🔍 Frontend: localToDbMapping:', localToDbMapping);
      
      Object.entries(variationImages).forEach(([variationId, imgData]) => {
        // Convert database ID to local ID for backend compatibility
        const localId = dbToLocalMapping[variationId] || variationId;
        console.log(`🔍 Frontend: Processing variation ${variationId} -> localId ${localId}`);
        console.log(`🔍 Frontend: Image data for ${variationId}:`, imgData);
        
        // Handle uploaded files for this variation
        if (imgData.uploadedImages.length > 0) {
          console.log(`🔍 Frontend: Adding ${imgData.uploadedImages.length} uploaded files for ${localId}`);
          imgData.uploadedImages.forEach((file, index) => {
            console.log(`🔍 Frontend: Adding file ${index}: variation_images_${localId}`, file.name);
            data.append(`variation_images_${localId}`, file);
          });
        }
        
        // Handle URL-based images for this variation
        if (imgData.imageUrls.length > 0) {
          console.log(`🔍 Frontend: Adding ${imgData.imageUrls.length} URL images for ${localId}`);
          // Reorder URLs so the main image is first
          const reorderedUrls = [...imgData.imageUrls];
          if (imgData.mainImageIndex > 0 && imgData.mainImageIndex < reorderedUrls.length) {
            const mainImage = reorderedUrls.splice(imgData.mainImageIndex, 1)[0];
            reorderedUrls.unshift(mainImage);
          }
          variationImageUrls[localId] = reorderedUrls;
          console.log(`🔍 Frontend: URLs for ${localId}:`, reorderedUrls);
        }
      });
      
      // Append variation image URLs if any exist
      if (Object.keys(variationImageUrls).length > 0) {
        console.log('🔍 Frontend: Sending variation_image_urls:', variationImageUrls);
        data.append('variation_image_urls', JSON.stringify(variationImageUrls));
      }
      
      // Send the mapping so backend can map local IDs back to database IDs
      if (Object.keys(localToDbMapping).length > 0) {
        console.log('🔍 Frontend: Sending variation_id_mapping:', localToDbMapping);
        data.append('variation_id_mapping', JSON.stringify(localToDbMapping));
      }
    }

    // Build final product data (for variations, recalc total stock from variationStocks)
    const finalData: ProductFormData = {
      ...formData,
      price: formData.price ?? 0,
      stock: variationType === 'none' ? formData.stock ?? 0 : calculateTotalStock(),
      minStock: variationType === 'none' ? formData.minStock ?? 0 : calculateMinStock(),
      variations:
        variationType === 'none'
          ? undefined
          : {
              ...formData.variations,
              stockByVariation: variationStocks
            }
    };

    // Append basic product fields
    data.append('name', finalData.name);
    data.append('sku', finalData.sku ?? '');
    data.append('price', String(finalData.price));
    data.append('category_id', String(finalData.categoryId));
    data.append('subcategory_id', String(finalData.subcategoryId));
    data.append('description', finalData.description ?? '');
    data.append('featured', String(finalData.featured));
    data.append('best_seller', String(finalData.bestSeller));
    data.append('new_arrival', String(finalData.newArrival));
    data.append('most_viewed', String(finalData.mostViewed));
    data.append('stock', String(finalData.stock));
    data.append('min_stock', String(finalData.minStock));
    data.append('product_type', finalProductType);

    if (finalData.variations) {
      data.append('variations', JSON.stringify(finalData.variations));
    }
    data.append('similarProducts', JSON.stringify(finalData.similarProducts || []));
    if (specifications.length > 0) {
      data.append('specifications', JSON.stringify(specifications));
    }

    try {
      await onSave(data, product?.id);
      toast.success(product ? 'Product updated successfully' : 'Product created successfully');
    } catch (error) {
      console.error('Failed to save product:', error);
      toast.error('Failed to save product');
    }
  };

  // Numeric Input Helper
  const safeNumberInput = (e: React.ChangeEvent<HTMLInputElement>, field: keyof ProductFormData) => {
    const raw = e.target.value;
    const val = raw === '' ? 0 : parseFloat(raw);
    setFormData(prev => ({ ...prev, [field]: isNaN(val) ? 0 : val }));
  };

  // Similar Product Options
  const similarProductOptions = useMemo(() => {
    if (!formData.categoryId) return [];
    return allProducts.filter(
      p => p.category_id === formData.categoryId && p.id !== product?.id
    );
  }, [allProducts, formData.categoryId, product]);

  // Show controls for variations (for color, size, age)
  const showColor = variationType.includes('color') || variationType === 'all';
  const showSize = variationType.includes('size') || variationType === 'all';
  const showAge = variationType.includes('age') || variationType === 'all';

  // Modify the categoryId change handler to fetch subcategories
  const handleCategoryChange = async (categoryId: number) => {
    setFormData(prev => ({ ...prev, categoryId, subcategoryId: 0 }));
    
    // Fetch subcategories for this category if needed
    if (fetchSubcategories && categoryId > 0) {
      await fetchSubcategories(categoryId);
    }
  };

  // Function to handle subcategoryId changes
  const handleSubcategoryChange = (subcategoryId: number) => {
    setFormData(prev => ({ ...prev, subcategoryId }));
  };

  const handleUploadImages = async () => {
    if (!product?.id) {
      toast.error('Please save the product first before uploading images');
      return;
    }

    const formData = new FormData();

    // Handle uploaded files
    if (uploadedImages.length > 0) {
      uploadedImages.forEach(file => {
        formData.append('productImages', file);
      });
    }

    // Handle image URLs
    if (imageUrls.length > 0) {
      formData.append('urls', JSON.stringify(imageUrls));
    }

    try {
      // Use the imageOperations prop to add images
      await imageOperations?.addImages(product.id, formData);
      
      // Refresh images after upload
      const updatedImages = await imageOperations?.getImages(product.id);
      if (updatedImages) {
        setImageUrls(updatedImages.map(img => {
          // Ensure URL includes full path with prefix
          const url = img.url || '';
          if (url.startsWith('http://') || url.startsWith('https://') || url.startsWith('data:')) {
            return url;
          }
          return backendBaseImagesURL + url;
        }));
      }
      
      // Clear the upload states
      setUploadedImages([]);
      setUploadPreviews([]);
      
      toast.success('Images uploaded successfully');
    } catch (error) {
      console.error('Failed to upload images:', error);
      toast.error('Failed to upload images');
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg w-full max-w-4xl h-[90vh] flex flex-col">
        {/* Header */}
        <div className="p-6 border-b flex-none">
          <div className="flex justify-between items-center">
            <h2 className="text-2xl font-semibold">
              {product ? 'Edit Product' : 'Add New Product'}
            </h2>
            <button onClick={onCancel} className="p-2 hover:bg-gray-100 rounded-full">
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-6">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Basic Information */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Product Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700">Product Name</label>
                <input
                  type="text"
                  value={formData.name ?? ''}
                  onChange={e => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-pink-500 focus:ring-pink-500"
                  required
                />
              </div>
              {/* SKU */}
              <div>
                <label className="block text-sm font-medium text-gray-700">SKU</label>
                <input
                  type="text"
                  value={formData.sku ?? ''}
                  onChange={e => setFormData(prev => ({ ...prev, sku: e.target.value }))}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-pink-500 focus:ring-pink-500"
                />
              </div>
              {/* Category */}
              <div>
                <label className="block text-sm font-medium text-gray-700">Category</label>
                <select
                  value={formData.categoryId ?? 0}
                  onChange={e => handleCategoryChange(parseInt(e.target.value, 10))}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-pink-500 focus:ring-pink-500"
                  required
                >
                  <option value={0}>Select Category</option>
                  {categories.map(cat => (
                    <option key={cat.id} value={cat.id}>
                      {cat.name}
                    </option>
                  ))}
                </select>
              </div>
              {/* Subcategory */}
              <div>
                <label className="block text-sm font-medium text-gray-700">Subcategory</label>
                <select
                  value={formData.subcategoryId ?? 0}
                  onChange={e => handleSubcategoryChange(parseInt(e.target.value, 10))}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-pink-500 focus:ring-pink-500"
                  disabled={!formData.categoryId}
                >
                  <option value={0}>Select Subcategory</option>
                  {subcategories && 
                   formData.categoryId > 0 &&
                   subcategories[formData.categoryId]?.map(subcat => (
                    <option key={subcat.id} value={subcat.id}>
                      {subcat.name}
                    </option>
                  ))}
                </select>
              </div>
              {/* Price */}
              <div>
                <label className="block text-sm font-medium text-gray-700">Price (KES)</label>
                <input
                  type="number"
                  value={formData.price ?? 0}
                  onChange={e => safeNumberInput(e, 'price')}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-pink-500 focus:ring-pink-500"
                  required
                  min="0"
                  step="0.01"
                />
              </div>
              {/* Description */}
              <div className="md:col-span-2 mb-10">
                <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                <div className="quill-container" style={{ height: '250px' }}>
                  <ReactQuill
                    value={formData.description ?? ''}
                    onChange={(value) => setFormData(prev => ({ ...prev, description: value }))}
                    theme="snow"
                    modules={{
                      toolbar: [
                        [{ 'header': [1, 2, 3, false] }],
                        ['bold', 'italic', 'underline', 'strike', 'blockquote'],
                        [{'list': 'ordered'}, {'list': 'bullet'}, {'indent': '-1'}, {'indent': '+1'}],
                        ['link'],
                        ['clean']
                      ],
                    }}
                    formats={[
                      'header',
                      'bold', 'italic', 'underline', 'strike', 'blockquote',
                      'list', 'bullet', 'indent',
                      'link'
                    ]}
                  />
                </div>
              </div>
            </div>

            {/* Product Variations */}
            <div className="space-y-4">
              <h3 className="text-sm font-medium text-gray-700">Product Variations</h3>
              <select
                value={variationType}
                onChange={e => handleVariationTypeChange(e.target.value as VariationType)}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-pink-500 focus:ring-pink-500"
                disabled={product && variationType !== 'none'}
              >
                <option value="none">No Variations</option>
                <option value="color">Color Only</option>
                <option value="size">Size Only</option>
                <option value="age">Age Range Only</option>
                <option value="color-age">Color + Age Range</option>
                <option value="color-size">Color + Size</option>
                <option value="age-size">Age Range + Size</option>
                <option value="all">Color + Size + Age</option>
              </select>

              {variationType !== 'none' && (
                <div className="space-y-4">
                  {/* Show color input if needed */}
                  {(variationType.includes('color') || variationType === 'all') && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Colors</label>
                      <div className="flex flex-wrap gap-2 mb-2">
                        {formData.variations.colors.map(color => (
                          <span key={color} className="inline-flex items-center gap-1 px-2 py-1 bg-gray-100 rounded-full text-sm">
                            {color}
                            <button
                              type="button"
                              onClick={() => handleRemoveVariation('color', color)}
                              className="p-1 hover:bg-gray-200 rounded-full"
                            >
                              <X className="h-3 w-3" />
                            </button>
                          </span>
                        ))}
                      </div>
                      <div className="flex gap-2">
                        <input
                          type="text"
                          value={newVariationValue}
                          onChange={e => setNewVariationValue(e.target.value)}
                          placeholder="Enter color"
                          className="flex-1 rounded-md border-gray-300 shadow-sm focus:border-pink-500 focus:ring-pink-500"
                          onKeyDown={e => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              handleAddVariation('color');
                            }
                          }}
                        />
                        <button
                          type="button"
                          onClick={() => handleAddVariation('color')}
                          className="px-4 py-2 bg-pink-500 text-white rounded-md hover:bg-pink-600"
                        >
                          Add
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Show size input if needed */}
                  {(variationType.includes('size') || variationType === 'all') && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Sizes</label>
                      <div className="flex flex-wrap gap-2 mb-2">
                        {formData.variations.sizes.map(sz => (
                          <span key={sz} className="inline-flex items-center gap-1 px-2 py-1 bg-gray-100 rounded-full text-sm">
                            {sz}
                            <button
                              type="button"
                              onClick={() => handleRemoveVariation('size', sz)}
                              className="p-1 hover:bg-gray-200 rounded-full"
                            >
                              <X className="h-3 w-3" />
                            </button>
                          </span>
                        ))}
                      </div>
                      <div className="flex gap-2">
                        <input
                          type="text"
                          value={newVariationValue}
                          onChange={e => setNewVariationValue(e.target.value)}
                          placeholder="Enter size"
                          className="flex-1 rounded-md border-gray-300 shadow-sm focus:border-pink-500 focus:ring-pink-500"
                          onKeyDown={e => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              handleAddVariation('size');
                            }
                          }}
                        />
                        <button
                          type="button"
                          onClick={() => handleAddVariation('size')}
                          className="px-4 py-2 bg-pink-500 text-white rounded-md hover:bg-pink-600"
                        >
                          Add
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Show age input if needed */}
                  {(variationType.includes('age') || variationType === 'all') && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Age Ranges</label>
                      <div className="flex flex-wrap gap-2 mb-2">
                        {formData.variations.ageRanges.map(age => (
                          <span key={age} className="inline-flex items-center gap-1 px-2 py-1 bg-gray-100 rounded-full text-sm">
                            {age}
                            <button
                              type="button"
                              onClick={() => handleRemoveVariation('ageRange', age)}
                              className="p-1 hover:bg-gray-200 rounded-full"
                            >
                              <X className="h-3 w-3" />
                            </button>
                          </span>
                        ))}
                      </div>
                      <div className="flex gap-2">
                        <input
                          type="text"
                          value={newVariationValue}
                          onChange={e => setNewVariationValue(e.target.value)}
                          placeholder="Enter age range"
                          className="flex-1 rounded-md border-gray-300 shadow-sm focus:border-pink-500 focus:ring-pink-500"
                          onKeyDown={e => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              handleAddVariation('ageRange');
                            }
                          }}
                        />
                        <button
                          type="button"
                          onClick={() => handleAddVariation('ageRange')}
                          className="px-4 py-2 bg-pink-500 text-white rounded-md hover:bg-pink-600"
                        >
                          Add
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Variation Stock Management */}
                  <div className="mt-6">
                    <div className="flex justify-between items-center mb-4">
                      <h4 className="text-sm font-medium text-gray-700">Stock by Variation</h4>
                      <button
                        type="button"
                        onClick={handleAddVariationStock}
                        className="flex items-center gap-2 text-pink-500 hover:text-pink-600"
                      >
                        <Plus className="h-5 w-5" />
                        Add Variation Stock
                      </button>
                    </div>
                    <div className="space-y-4">
                      {variationStocks.map(vs => {
                        const imgData = variationImages[vs.id] || getDefaultVariationImageData();
                        // Create a description from available fields
                        const variationDesc = [
                          vs.color ? `Color: ${vs.color}` : '',
                          vs.size ? `Size: ${vs.size}` : '',
                          vs.ageRange ? `Age: ${vs.ageRange}` : ''
                        ].filter(Boolean).join(', ');
                        
                        return (
                          <div key={vs.id} className="space-y-4">
                            {/* Variation Details Card */}
                            <div className="p-4 bg-gray-50 rounded-lg">
                              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                                {(variationType.includes('color') || variationType === 'all') && (
                                  <div>
                                    <label className="block text-sm text-gray-600 mb-1">Color</label>
                                    <select
                                      value={vs.color ?? ''}
                                      onChange={e => handleUpdateVariationStock(vs.id, 'color', e.target.value)}
                                      className="w-full rounded-md border-gray-300 shadow-sm focus:border-pink-500 focus:ring-pink-500"
                                    >
                                      <option value="">Select Color</option>
                                      {formData.variations.colors.map(c => (
                                        <option key={c} value={c}>{c}</option>
                                      ))}
                                    </select>
                                  </div>
                                )}
                                {/* Similar blocks for size and age */}
                                {(variationType.includes('size') || variationType === 'all') && (
                                  <div>
                                    <label className="block text-sm text-gray-600 mb-1">Size</label>
                                    <select
                                      value={vs.size ?? ''}
                                      onChange={e => handleUpdateVariationStock(vs.id, 'size', e.target.value)}
                                      className="w-full rounded-md border-gray-300 shadow-sm focus:border-pink-500 focus:ring-pink-500"
                                    >
                                      <option value="">Select Size</option>
                                      {formData.variations.sizes.map(s => (
                                        <option key={s} value={s}>{s}</option>
                                      ))}
                                    </select>
                                  </div>
                                )}
                                {(variationType.includes('age') || variationType === 'all') && (
                                  <div>
                                    <label className="block text-sm text-gray-600 mb-1">Age Range</label>
                                    <select
                                      value={vs.ageRange ?? ''}
                                      onChange={e => handleUpdateVariationStock(vs.id, 'ageRange', e.target.value)}
                                      className="w-full rounded-md border-gray-300 shadow-sm focus:border-pink-500 focus:ring-pink-500"
                                    >
                                      <option value="">Select Age Range</option>
                                      {formData.variations.ageRanges.map(a => (
                                        <option key={a} value={a}>{a}</option>
                                      ))}
                                    </select>
                                  </div>
                                )}
                                {/* Stock and Min Stock fields side by side */}
                                <div className="grid grid-cols-2 gap-4">
                                  <div>
                                    <label className="block text-sm text-gray-600 mb-1">Stock</label>
                                    <input
                                      type="number"
                                      value={vs.stock}
                                      onChange={e => handleUpdateVariationStock(vs.id, 'stock', parseInt(e.target.value) || 0)}
                                      min="0"
                                      className="w-full rounded-md border-gray-300 shadow-sm focus:border-pink-500 focus:ring-pink-500"
                                    />
                                  </div>
                                  <div>
                                    <label className="block text-sm text-gray-600 mb-1">Min Stock</label>
                                    <div className="flex gap-2">
                                      <input
                                        type="number"
                                        value={vs.minStock}
                                        onChange={e => handleUpdateVariationStock(vs.id, 'minStock', parseInt(e.target.value) || 0)}
                                        min="0"
                                        className="w-full rounded-md border-gray-300 shadow-sm focus:border-pink-500 focus:ring-pink-500"
                                      />
                                      <button
                                        type="button"
                                        onClick={() => setVariationStocks(prev => prev.filter(s => s.id !== vs.id))}
                                        className="p-2 text-red-500 hover:bg-red-50 rounded-md"
                                      >
                                        <X className="h-5 w-5" />
                                      </button>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>

                            {/* Variation Images Card */}
                            <div className="p-4 bg-white border border-gray-200 rounded-lg">
                              <div className="space-y-4">
                                <div className="flex justify-between items-center">
                                  <h5 className="text-sm font-medium text-gray-700">
                                    Images for {variationDesc}
                                  </h5>
                                  <div className="flex gap-2">
                                    <button
                                      type="button"
                                      onClick={() => updateVariationImageType(vs.id, 'upload')}
                                      className={`flex items-center gap-1 px-3 py-1 rounded-md text-sm ${
                                        imgData.imageType === 'upload' ? 'bg-pink-500 text-white' : 'bg-gray-100 text-gray-700'
                                      }`}
                                    >
                                      <Upload className="h-4 w-4" />
                                      Upload
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() => updateVariationImageType(vs.id, 'link')}
                                      className={`flex items-center gap-1 px-3 py-1 rounded-md text-sm ${
                                        imgData.imageType === 'link' ? 'bg-pink-500 text-white' : 'bg-gray-100 text-gray-700'
                                      }`}
                                    >
                                      <LinkIcon className="h-4 w-4" />
                                      URLs
                                    </button>
                                  </div>
                                </div>

                                {/* Existing Variation Images Display */}
                                {imgData.imageUrls.length > 0 && (
                                  <div className="mb-4">
                                    <h6 className="text-xs font-medium text-gray-600 mb-2">Current Images</h6>
                                    <div className="grid grid-cols-4 gap-2">
                                      {imgData.imageUrls.map((url, index) => (
                                        <div key={index} className="relative">
                                          <img
                                            src={url}
                                            alt={`Variation ${index + 1}`}
                                            className="w-full h-20 object-cover rounded-lg"
                                          />
                                          <div className="absolute top-1 left-1">
                                            <input
                                              type="radio"
                                              name={`variation-main-image-${vs.id}`}
                                              checked={imgData.mainImageIndex === index}
                                              onChange={() => handleSetVariationMainImageRealTime(vs.id, index)}
                                              className="sr-only"
                                              id={`variation-main-image-${vs.id}-${index}`}
                                            />
                                            <label
                                              htmlFor={`variation-main-image-${vs.id}-${index}`}
                                              className={`px-1 py-0.5 rounded text-xs cursor-pointer ${
                                                imgData.mainImageIndex === index ? 'bg-pink-500 text-white' : 'bg-white text-gray-700'
                                              }`}
                                            >
                                              {imgData.mainImageIndex === index ? 'Main' : 'Set'}
                                            </label>
                                          </div>
                                          <button
                                            type="button"
                                            onClick={() => handleRemoveVariationUrl(vs.id, index)}
                                            className="absolute -top-1 -right-1 p-0.5 bg-red-500 text-white rounded-full hover:bg-red-600"
                                          >
                                            <X className="h-3 w-3" />
                                          </button>
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                )}

                                {imgData.imageType === 'upload' && (
                                  <div className="space-y-4">
                                    <input
                                      type="file"
                                      multiple
                                      accept="image/*"
                                      onChange={e => handleVariationFileChange(vs.id, e.target.files)}
                                      className="hidden"
                                      id={`variation-upload-${vs.id}`}
                                    />
                                    <label
                                      htmlFor={`variation-upload-${vs.id}`}
                                      className="block w-full border-2 border-dashed border-gray-300 rounded-lg p-4 text-center cursor-pointer hover:border-pink-500"
                                    >
                                      <Upload className="h-6 w-6 mx-auto text-gray-400" />
                                      <span className="mt-2 block text-sm text-gray-600">Click to upload images</span>
                                    </label>

                                    {/* Image Previews */}
                                    {imgData.uploadPreviews.length > 0 && (
                                      <div className="grid grid-cols-4 gap-4">
                                        {imgData.uploadPreviews.map((preview, index) => (
                                          <div key={index} className="relative">
                                            <img
                                              src={preview}
                                              alt={`Preview ${index + 1}`}
                                              className="w-full h-24 object-cover rounded-lg"
                                            />
                                            <div className="absolute top-2 left-2">
                                              <input
                                                type="radio"
                                                name={`main-image-${vs.id}`}
                                                checked={imgData.mainImageIndex === index}
                                                onChange={() => handleSetVariationMainImageRealTime(vs.id, index)}
                                                className="sr-only"
                                                id={`main-image-${vs.id}-${index}`}
                                              />
                                              <label
                                                htmlFor={`main-image-${vs.id}-${index}`}
                                                className={`px-2 py-1 rounded-full text-xs ${
                                                  imgData.mainImageIndex === index
                                                    ? 'bg-pink-500 text-white'
                                                    : 'bg-white text-gray-700'
                                                }`}
                                              >
                                                {imgData.mainImageIndex === index ? 'Main' : 'Set as Main'}
                                              </label>
                                            </div>
                                            <button
                                              type="button"
                                              onClick={() => handleRemoveVariationFile(vs.id, index)}
                                              className="absolute -top-2 -right-2 p-1 bg-red-500 text-white rounded-full"
                                            >
                                              <X className="h-4 w-4" />
                                            </button>
                                          </div>
                                        ))}
                                      </div>
                                    )}

                                    {/* Add Upload New Images Button for uploads */}
                                    {(imgData.uploadedImages.length > 0 || imgData.imageUrls.length > 0) && (
                                      <button
                                        type="button"
                                        onClick={() => handleUploadVariationImages(vs.id)}
                                        className="mt-4 px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 flex items-center gap-2"
                                      >
                                        <Upload className="h-5 w-5" />
                                        Upload New Images
                                      </button>
                                    )}
                                  </div>
                                )}

                                {imgData.imageType === 'link' && (
                                  <div className="space-y-2">
                                    {/* Add URL Input for new images */}
                                    <div className="flex gap-2 items-center">
                                      <input
                                        type="url"
                                        placeholder="Add new image URL"
                                        className="flex-1 rounded-md border-gray-300 shadow-sm focus:border-pink-500 focus:ring-pink-500"
                                        onKeyPress={(e) => {
                                          if (e.key === 'Enter') {
                                            e.preventDefault();
                                            const input = e.target as HTMLInputElement;
                                            if (input.value.trim()) {
                                              setVariationImages(prev => ({
                                                ...prev,
                                                [vs.id]: { ...imgData, imageUrls: [...imgData.imageUrls, input.value.trim()] }
                                              }));
                                              input.value = '';
                                            }
                                          }
                                        }}
                                      />
                                      <button
                                        type="button"
                                        onClick={(e) => {
                                          const input = (e.target as HTMLButtonElement).previousElementSibling as HTMLInputElement;
                                          if (input.value.trim()) {
                                            setVariationImages(prev => ({
                                              ...prev,
                                              [vs.id]: { ...imgData, imageUrls: [...imgData.imageUrls, input.value.trim()] }
                                            }));
                                            input.value = '';
                                          }
                                        }}
                                        className="flex items-center gap-1 px-3 py-2 bg-pink-500 text-white rounded-md hover:bg-pink-600"
                                      >
                                        <Plus className="h-4 w-4" />
                                        Add
                                      </button>
                                    </div>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* New Inventory Inputs for simple (no variation) products */}
            {variationType === 'none' && (
              <div className="space-y-4">
                <h3 className="text-sm font-medium text-gray-700">Inventory</h3>
                <div className="grid grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Stock</label>
                    <input
                      type="number"
                      value={formData.stock}
                      onChange={e => safeNumberInput(e, 'stock')}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-pink-500 focus:ring-pink-500"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Minimum Stock</label>
                    <input
                      type="number"
                      value={formData.minStock}
                      onChange={e => safeNumberInput(e, 'minStock')}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-pink-500 focus:ring-pink-500"
                      required
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Conditional Global Images (only for simple products) */}
            {variationType === 'none' && (
              <div className="space-y-4">
                <h3 className="text-sm font-medium text-gray-700">Product Images</h3>
                
                {/* Image Type Selection */}
                <div className="flex gap-4">
                  <button
                    type="button"
                    onClick={() => setImageType('upload')}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg ${
                      imageType === 'upload' ? 'bg-pink-500 text-white' : 'bg-gray-100 text-gray-700'
                    }`}
                  >
                    <Upload className="h-5 w-5" />
                    Upload Images
                  </button>
                  <button
                    type="button"
                    onClick={() => setImageType('link')}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg ${
                      imageType === 'link' ? 'bg-pink-500 text-white' : 'bg-gray-100 text-gray-700'
                    }`}
                  >
                    <LinkIcon className="h-5 w-5" />
                    Image URLs
                  </button>
                </div>

                {/* Existing Images Display */}
                {product?.images && product.images.length > 0 && (
                  <div className="mb-4">
                    <h4 className="text-sm font-medium text-gray-700 mb-2">Current Images</h4>
                    <div className="grid grid-cols-4 gap-4">
                      {product.images.map((image: any, index: number) => (
                        <div key={image.id} className="relative">
                          <img
                            src={getFullImageUrl(image)}
                            alt={`Product ${index + 1}`}
                            className="w-full h-24 object-cover rounded-lg"
                          />
                          <div className="absolute top-2 left-2">
                            <input
                              type="radio"
                              name="existingMainImage"
                              checked={image.is_primary === 1}
                              onChange={() => handleSetMainImageRealTime(image.id)}
                              className="sr-only"
                              id={`existing-main-image-${image.id}`}
                            />
                            <label
                              htmlFor={`existing-main-image-${image.id}`}
                              className={`px-2 py-1 rounded-full text-xs ${
                                image.is_primary === 1 ? 'bg-pink-500 text-white' : 'bg-white text-gray-700'
                              }`}
                            >
                              {image.is_primary === 1 ? 'Main' : 'Set as Main'}
                            </label>
                          </div>
                          <button
                            type="button"
                            onClick={() => handleRemoveImage(image.id)}
                            className="absolute -top-2 -right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600"
                          >
                            <X className="h-4 w-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Upload Section */}
                {imageType === 'upload' && (
                  <div className="space-y-4">
                    <input
                      type="file"
                      multiple
                      accept="image/*"
                      onChange={e => {
                        const files = Array.from(e.target.files || []);
                        setUploadedImages(prev => [...prev, ...files]);
                        files.forEach(file => {
                          const reader = new FileReader();
                          reader.onloadend = () => {
                            setUploadPreviews(prev => [...prev, reader.result as string]);
                          };
                          reader.readAsDataURL(file);
                        });
                      }}
                      className="hidden"
                      id="image-upload"
                    />
                    <label
                      htmlFor="image-upload"
                      className="block w-full border-2 border-dashed border-gray-300 rounded-lg p-4 text-center cursor-pointer hover:border-pink-500"
                    >
                      <Upload className="h-8 w-8 mx-auto text-gray-400" />
                      <span className="mt-2 block text-sm text-gray-600">Click to upload new images</span>
                    </label>

                    {/* New Image Previews with Upload Button */}
                    {uploadPreviews.length > 0 && (
                      <>
                        <h4 className="text-sm font-medium text-gray-700">New Images to Upload</h4>
                        <div className="grid grid-cols-4 gap-4">
                          {uploadPreviews.map((preview, index) => (
                            <div key={`new-${index}`} className="relative">
                              <img
                                src={preview}
                                alt={`New upload ${index + 1}`}
                                className="w-full h-24 object-cover rounded-lg"
                              />
                              <button
                                type="button"
                                onClick={() => {
                                  setUploadedImages(prev => prev.filter((_, i) => i !== index));
                                  setUploadPreviews(prev => prev.filter((_, i) => i !== index));
                                }}
                                className="absolute -top-2 -right-2 p-1 bg-red-500 text-white rounded-full"
                              >
                                <X className="h-4 w-4" />
                              </button>
                            </div>
                          ))}
                        </div>
                        
                        {/* Add Upload Images Button */}
                        <button
                          type="button"
                          onClick={handleUploadImages}
                          className="mt-4 px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 flex items-center gap-2"
                        >
                          <Upload className="h-5 w-5" />
                          Upload New Images
                        </button>
                      </>
                    )}
                  </div>
                )}

                {/* URL Section */}
                {imageType === 'link' && (
                  <div className="space-y-4">
                    {imageUrls.map((url, index) => {
                      const combinedIndex = uploadPreviews.length + index;
                      return (
                        <div key={index} className="flex gap-2 items-center">
                          <input
                            type="radio"
                            name="mainImage"
                            checked={mainImageIndex === combinedIndex}
                            onChange={() => setMainImageIndex(combinedIndex)}
                            className="h-4 w-4 text-pink-500"
                          />
                          <input
                            type="url"
                            value={url}
                            onChange={e => {
                              const newUrls = [...imageUrls];
                              newUrls[index] = e.target.value;
                              setImageUrls(newUrls);
                            }}
                            placeholder="Enter image URL"
                            className="flex-1 rounded-md border-gray-300 shadow-sm focus:border-pink-500 focus:ring-pink-500"
                          />
                          {url && (
                            <button
                              type="button"
                              onClick={() => window.open(url, '_blank')}
                              className="p-2 text-gray-500 hover:bg-gray-100 rounded-md"
                            >
                              <ExternalLink className="h-5 w-5" />
                            </button>
                          )}
                          <button
                            type="button"
                            onClick={() => {
                              const newUrls = imageUrls.filter((_, i) => i !== index);
                              setImageUrls(newUrls);
                              if (mainImageIndex === combinedIndex) {
                                setMainImageIndex(0);
                              }
                            }}
                            className="p-2 text-red-500 hover:bg-red-50 rounded-md"
                          >
                            <X className="h-5 w-5" />
                          </button>
                        </div>
                      );
                    })}
                    <button
                      type="button"
                      onClick={() => setImageUrls([...imageUrls, ''])}
                      className="flex items-center gap-2 text-pink-500 hover:text-pink-600"
                    >
                      <Plus className="h-5 w-5" />
                      Add Image URL
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* Similar Products */}
            <div className="space-y-4">
              <h3 className="text-sm font-medium text-gray-700">Similar Products</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {similarProductOptions.map(simProd => {
                  const simMainImg = simProd.images?.[0]
                    ? getFullImageUrl(simProd.images[0])
                    : 'https://via.placeholder.com/150/CCCCCC/000000?text=No+Image';
                  return (
                    <label key={simProd.id} className="relative cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.similarProducts?.includes(simProd.id)}
                        onChange={e => {
                          const checked = e.target.checked;
                          setFormData(prev => ({
                            ...prev,
                            similarProducts: checked
                              ? [...(prev.similarProducts || []), simProd.id]
                              : (prev.similarProducts || []).filter(id => id !== simProd.id)
                          }));
                        }}
                        className="sr-only"
                      />
                      <div
                        className={`rounded-lg overflow-hidden border-2 ${
                          formData.similarProducts?.includes(simProd.id)
                            ? 'border-pink-500'
                            : 'border-transparent'
                        }`}
                      >
                        <img
                          src={simMainImg}
                          alt={simProd.name}
                          className="w-full h-32 object-cover"
                        />
                        <div className="p-2">
                          <p className="text-sm font-medium truncate">{simProd.name}</p>
                          <p className="text-xs text-gray-500">
                            KES {simProd.price?.toLocaleString() || '0'}
                          </p>
                        </div>
                      </div>
                    </label>
                  );
                })}
              </div>
            </div>

            {/* Product Specifications */}
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <h3 className="text-sm font-medium text-gray-700">Product Specifications</h3>
                <button
                  type="button"
                  onClick={handleAddSpecification}
                  className="inline-flex items-center px-3 py-1 border border-transparent text-sm font-medium rounded-md text-white bg-pink-600 hover:bg-pink-700"
                >
                  <Plus className="h-4 w-4 mr-1" />
                  Add Specification
                </button>
              </div>
              {specifications.map(spec => (
                <div key={spec.id} className="flex gap-4 items-center">
                  <input
                    type="text"
                    value={spec.spec_key}
                    onChange={(e) => handleSpecificationChange(spec.id, 'spec_key', e.target.value)}
                    placeholder="Specification"
                    className="flex-1 rounded-md border-gray-300 shadow-sm focus:border-pink-500 focus:ring-pink-500"
                  />
                  <input
                    type="text"
                    value={spec.spec_value}
                    onChange={(e) => handleSpecificationChange(spec.id, 'spec_value', e.target.value)}
                    placeholder="Details"
                    className="flex-1 rounded-md border-gray-300 shadow-sm focus:border-pink-500 focus:ring-pink-500"
                  />
                  <button
                    type="button"
                    onClick={() => handleRemoveSpecification(spec.id)}
                    className="p-2 text-gray-400 hover:text-red-500"
                  >
                    <X className="h-5 w-5" />
                  </button>
                </div>
              ))}
            </div>

            {/* Product Status */}
            <div className="space-y-4">
              <h3 className="text-sm font-medium text-gray-700">Product Status</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={formData.featured ?? false}
                    onChange={e => setFormData(prev => ({ ...prev, featured: e.target.checked }))}
                    className="rounded text-pink-500 focus:ring-pink-500"
                  />
                  <span className="text-sm text-gray-700">Featured</span>
                </label>
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={formData.bestSeller ?? false}
                    onChange={e => setFormData(prev => ({ ...prev, bestSeller: e.target.checked }))}
                    className="rounded text-pink-500 focus:ring-pink-500"
                  />
                  <span className="text-sm text-gray-700">Best Seller</span>
                </label>
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={formData.newArrival ?? false}
                    onChange={e => setFormData(prev => ({ ...prev, newArrival: e.target.checked }))}
                    className="rounded text-pink-500 focus:ring-pink-500"
                  />
                  <span className="text-sm text-gray-700">New Arrival</span>
                </label>
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={formData.mostViewed ?? false}
                    onChange={e => setFormData(prev => ({ ...prev, mostViewed: e.target.checked }))}
                    className="rounded text-pink-500 focus:ring-pink-500"
                  />
                  <span className="text-sm text-gray-700">Most Viewed</span>
                </label>
              </div>
            </div>

            {/* Form Actions */}
            <div className="flex justify-end gap-4">
              <button
                type="button"
                onClick={onCancel}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-4 py-2 bg-pink-500 text-white rounded-lg hover:bg-pink-600"
              >
                {product ? 'Update Product' : 'Create Product'}
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* Specifications Modal */}
      {isSpecModalOpen && (
        <ProductSpecificationsModal
          productId={product?.id || 0}
          onClose={() => setIsSpecModalOpen(false)}
        />
      )}
    </div>
  );
};
